import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import RetailPOSPaymentWorkspace
  from "./RetailPOSPaymentWorkspace";

import RetailPOSTicketWorkspace
  from "./RetailPOSTicketWorkspace";

import RetailPOSReturnRefundWorkspace
  from "./RetailPOSReturnRefundWorkspace";

import RetailPOSCashSessionWorkspace
  from "./RetailPOSCashSessionWorkspace";

import PropTypes from "prop-types";

import {
  isRetailPOSRuntime,
} from "./retailPOSRuntime";

import "./RetailPOSWorkspace.css";


export function createRetailPOSWorkspaceComponent({
  runtime,
} = {}) {
  if (
    !isRetailPOSRuntime(
      runtime
    )
  ) {
    throw new TypeError(
      "createRetailPOSWorkspaceComponent requires a RetailPOSRuntime."
    );
  }

  function BoundRetailPOSWorkspace(
    props
  ) {
    return (
      <RetailPOSWorkspace
        {...props}
        runtime={runtime}
      />
    );
  }

  BoundRetailPOSWorkspace.displayName =
    "BoundRetailPOSWorkspace";

  return BoundRetailPOSWorkspace;
}


export default function RetailPOSWorkspace({
  runtime,
  resolvedPOSActivityDomain,
  runtimeConfiguration,
  publication,
  workspaceDescriptor,
  navigation = [],
  actions = [],
}) {
  if (
    !isRetailPOSRuntime(
      runtime
    )
  ) {
    throw new TypeError(
      "RetailPOSWorkspace requires a RetailPOSRuntime."
    );
  }


  const context =
    useMemo(
      () =>
        Object.freeze({
          companyUuid:
            resolvedPOSActivityDomain
              ?.companyUuid
            ?? runtimeConfiguration
              ?.companyUuid,

          activityUuid:
            resolvedPOSActivityDomain
              ?.activityUuid
            ?? runtimeConfiguration
              ?.activityUuid,
        }),
      [
        resolvedPOSActivityDomain,
        runtimeConfiguration,
      ]
    );


  const [
    scanCode,
    setScanCode,
  ] =
    useState("");


  const [
    searchQuery,
    setSearchQuery,
  ] =
    useState("");


  const [
    searchResults,
    setSearchResults,
  ] =
    useState([]);


  const [
    searchLoading,
    setSearchLoading,
  ] =
    useState(false);


  const [
    lastProduct,
    setLastProduct,
  ] =
    useState(null);


  const [
    status,
    setStatus,
  ] =
    useState(null);


  const [
    cartSnapshot,
    setCartSnapshot,
  ] =
    useState(null);


  const [
    cartLoading,
    setCartLoading,
  ] =
    useState(false);


  const [
    cartError,
    setCartError,
  ] =
    useState(null);


  const [
    cartPendingAction,
    setCartPendingAction,
  ] =
    useState(null);


  const [
    customerQuery,
    setCustomerQuery,
  ] =
    useState("");


  const [
    customerResults,
    setCustomerResults,
  ] =
    useState([]);


  const [
    selectedCustomer,
    setSelectedCustomer,
  ] =
    useState(null);


  const [
    customerLoading,
    setCustomerLoading,
  ] =
    useState(false);


  const [
    customerError,
    setCustomerError,
  ] =
    useState(null);


  const [
    loyaltySnapshot,
    setLoyaltySnapshot,
  ] =
    useState(null);


  const [
    loyaltyLoading,
    setLoyaltyLoading,
  ] =
    useState(false);


  const [
    loyaltyError,
    setLoyaltyError,
  ] =
    useState(null);


  const [
    discountType,
    setDiscountType,
  ] =
    useState("percent");


  const [
    discountValue,
    setDiscountValue,
  ] =
    useState("");


  const [
    discountScope,
    setDiscountScope,
  ] =
    useState("cart");


  const [
    selectedDiscountTarget,
    setSelectedDiscountTarget,
  ] =
    useState("");


  const [
    preparedDiscountIntent,
    setPreparedDiscountIntent,
  ] =
    useState(null);


  const [
    selectedBenefit,
    setSelectedBenefit,
  ] =
    useState(null);


  const [
    checkoutLoading,
    setCheckoutLoading,
  ] =
    useState(false);


  const [
    checkoutError,
    setCheckoutError,
  ] =
    useState(null);


  const [
    receiptSnapshot,
    setReceiptSnapshot,
  ] =
    useState(null);


  const checkoutInFlightRef =
    useRef(false);


  const checkoutMountedRef =
    useRef(true);


  useEffect(
    () => {
      checkoutMountedRef.current =
        true;

      return () => {
        checkoutMountedRef.current =
          false;
      };
    },
    []
  );


  const capabilities =
    runtime.getCapabilities();


  const refreshCartSnapshot =
    useCallback(
      async () => {
        if (
          !runtime
            .getCapabilities()
            .cart
        ) {
          setCartSnapshot(null);
          setCartError(null);
          return null;
        }

        setCartLoading(true);
        setCartError(null);

        try {
          const result =
            await runtime.loadCart(
              context
            );

          const snapshot =
            result?.data
            ?? result
            ?? {};

          setCartSnapshot(
            snapshot
          );

          return snapshot;
        } catch (error) {
          setCartError(
            error?.message
            ?? "Impossible de charger le panier."
          );

          return null;
        } finally {
          setCartLoading(false);
        }
      },
      [
        runtime,
        context,
      ]
    );


  useEffect(
    () => {
      let active = true;

      async function load() {
        if (
          !runtime
            .getCapabilities()
            .cart
        ) {
          return;
        }

        setCartLoading(true);
        setCartError(null);

        try {
          const result =
            await runtime.loadCart(
              context
            );

          if (!active) {
            return;
          }

          setCartSnapshot(
            result?.data
            ?? result
            ?? {}
          );
        } catch (error) {
          if (!active) {
            return;
          }

          setCartError(
            error?.message
            ?? "Impossible de charger le panier."
          );
        } finally {
          if (active) {
            setCartLoading(false);
          }
        }
      }

      load();

      return () => {
        active = false;
      };
    },
    [
      runtime,
      context,
    ]
  );


  async function handleCustomerSearch() {
    const query =
      customerQuery.trim();

    if (!query) {
      setCustomerResults([]);
      setCustomerError(null);
      return;
    }

    if (!capabilities.customerSearch) {
      setCustomerResults([]);
      setCustomerError(
        "Recherche client indisponible."
      );
      return;
    }

    setCustomerLoading(true);
    setCustomerError(null);

    try {
      const customers =
        await runtime.searchCustomers(
          query,
          context
        );

      setCustomerResults(
        customers
      );
    } catch (error) {
      setCustomerResults([]);

      setCustomerError(
        error?.message
        ?? "Impossible de rechercher les clients."
      );
    } finally {
      setCustomerLoading(false);
    }
  }


  async function handleSelectCustomer(
    customer
  ) {
    setSelectedCustomer(
      customer
    );

    setCustomerResults([]);
    setCustomerError(null);

    if (!capabilities.loyalty) {
      setLoyaltySnapshot(null);
      setLoyaltyError(null);
      return;
    }

    setLoyaltyLoading(true);
    setLoyaltyError(null);

    try {
      const result =
        await runtime.resolveLoyalty(
          customer,
          context
        );

      setLoyaltySnapshot(
        result?.data
        ?? result
        ?? null
      );
    } catch (error) {
      setLoyaltySnapshot(null);

      setLoyaltyError(
        error?.message
        ?? "Impossible de charger la fidélité."
      );
    } finally {
      setLoyaltyLoading(false);
    }
  }


  function handleClearCustomer() {
    setSelectedCustomer(null);
    setCustomerResults([]);
    setCustomerError(null);

    setLoyaltySnapshot(null);
    setLoyaltyError(null);
    setLoyaltyLoading(false);

    setSelectedBenefit(null);
  }


  function handlePrepareDiscountIntent(
    event
  ) {
    event.preventDefault();

    const normalizedValue =
      String(
        discountValue
      )
        .trim()
        .replace(
          ",",
          "."
        );

    const numericValue =
      Number(
        normalizedValue
      );

    if (
      !Number.isFinite(
        numericValue
      )
      || numericValue <= 0
    ) {
      return;
    }

    if (
      discountType
        === "percent"
      && numericValue > 100
    ) {
      return;
    }

    let target = null;

    if (
      discountScope
        === "line"
    ) {
      const line =
        cartItems.find(
          (
            item,
            index
          ) => {
            const key =
              String(
                item.uuid
                ?? item.productUuid
                ?? item
                  .activity_product_uuid
                ?? `cart-line-${index}`
              );

            return (
              key
              === selectedDiscountTarget
            );
          }
        );

      if (!line) {
        return;
      }

      target =
        Object.freeze({
          cartLineUuid:
            line.uuid
            ?? null,

          productUuid:
            line.productUuid
            ?? line
              .activity_product_uuid
            ?? line.product_uuid
            ?? null,
        });
    }

    setPreparedDiscountIntent(
      Object.freeze({
        scope:
          discountScope,

        type:
          discountType,

        value:
          numericValue,

        target,
      })
    );
  }


  function handleClearDiscountIntent() {
    setPreparedDiscountIntent(null);
  }


  function handleSelectLoyaltyBenefit(
    benefit
  ) {
    setSelectedBenefit(
      benefit
    );
  }


  function handleClearLoyaltyBenefit() {
    setSelectedBenefit(null);
  }


  async function handleProductSearch() {
    const query =
      searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      setStatus(null);
      return;
    }

    if (!capabilities.productSearch) {
      setSearchResults([]);
      setStatus(
        "search-unavailable"
      );
      return;
    }

    setSearchLoading(true);
    setStatus(
      "search-pending"
    );

    try {
      const products =
        await runtime.searchProducts(
          query,
          context
        );

      setSearchResults(
        products
      );

      setStatus(
        products.length > 0
          ? "search-resolved"
          : "search-empty"
      );
    } catch {
      setSearchResults([]);
      setStatus(
        "search-error"
      );
    } finally {
      setSearchLoading(false);
    }
  }


  function handleSelectProduct(
    product
  ) {
    setLastProduct(
      product
    );

    setStatus(
      "product-selected"
    );
  }


  async function handleScan() {
    if (
      typeof scanCode
        !== "string"
      || scanCode.trim()
        === ""
    ) {
      return;
    }

    setStatus(
      "scan-pending"
    );

    try {
      const product =
        await runtime.scanProduct(
          scanCode.trim(),
          context
        );

      setLastProduct(
        product
      );

      setStatus(
        "scan-resolved"
      );
    } catch {
      setStatus(
        "scan-error"
      );
    }
  }


  async function handleAddProduct() {
    if (!lastProduct) {
      return;
    }

    setStatus(
      "cart-pending"
    );

    try {
      await runtime.addProduct(
        lastProduct,
        1,
        context
      );

      await refreshCartSnapshot();

      setStatus(
        "cart-updated"
      );
    } catch {
      setStatus(
        "cart-error"
      );
    }
  }


  async function handleIncrementCartLine(
    line
  ) {
    const lineId =
      line.uuid
      ?? line.productUuid
      ?? line.activity_product_uuid
      ?? "line";

    setCartPendingAction(
      `increment:${lineId}`
    );

    setCartError(null);

    try {
      await runtime.addProduct(
        line,
        1,
        context
      );

      await refreshCartSnapshot();

      setStatus(
        "cart-updated"
      );
    } catch (error) {
      setCartError(
        error?.message
        ?? "Impossible d'augmenter la quantité."
      );

      setStatus(
        "cart-error"
      );
    } finally {
      setCartPendingAction(null);
    }
  }


  async function handleDecrementCartLine(
    line
  ) {
    const lineId =
      line.uuid
      ?? line.productUuid
      ?? line.activity_product_uuid
      ?? "line";

    setCartPendingAction(
      `decrement:${lineId}`
    );

    setCartError(null);

    try {
      if (
        Number(
          line.quantity
          ?? 0
        ) > 1
      ) {
        await runtime
          .decrementProduct(
            line,
            context
          );
      } else {
        await runtime
          .removeProduct(
            line,
            context
          );
      }

      await refreshCartSnapshot();

      setStatus(
        "cart-updated"
      );
    } catch (error) {
      setCartError(
        error?.message
        ?? "Impossible de diminuer la quantité."
      );

      setStatus(
        "cart-error"
      );
    } finally {
      setCartPendingAction(null);
    }
  }


  async function handleRemoveCartLine(
    line
  ) {
    const lineId =
      line.uuid
      ?? line.productUuid
      ?? line.activity_product_uuid
      ?? "line";

    setCartPendingAction(
      `remove:${lineId}`
    );

    setCartError(null);

    try {
      await runtime.removeProduct(
        line,
        context
      );

      await refreshCartSnapshot();

      setStatus(
        "cart-updated"
      );
    } catch (error) {
      setCartError(
        error?.message
        ?? "Impossible de supprimer l'article."
      );

      setStatus(
        "cart-error"
      );
    } finally {
      setCartPendingAction(null);
    }
  }


  async function handleClearCart() {
    setCartPendingAction(
      "clear"
    );

    setCartError(null);

    try {
      await runtime.clearCart(
        context
      );

      await refreshCartSnapshot();

      setStatus(
        "cart-updated"
      );
    } catch (error) {
      setCartError(
        error?.message
        ?? "Impossible de vider le panier."
      );

      setStatus(
        "cart-error"
      );
    } finally {
      setCartPendingAction(null);
    }
  }


  async function handleCheckout() {
    if (
      checkoutInFlightRef.current
      || !capabilities.checkout
    ) {
      return;
    }

    checkoutInFlightRef.current =
      true;

    setCheckoutLoading(true);
    setCheckoutError(null);
    setReceiptSnapshot(null);

    try {
      const checkoutResult =
        await runtime.checkout(
          {
            payments: [],

            customer:
              selectedCustomer
              ?? null,
          },
          context
        );

      const salesReceiptUuid =
        checkoutResult
          ?.salesReceiptUuid;

      if (
        typeof salesReceiptUuid
          !== "string"
        || !salesReceiptUuid.trim()
      ) {
        throw new TypeError(
          "Checkout must return a non-empty salesReceiptUuid."
        );
      }

      const authoritativeReceipt =
        await runtime.resolveReceipt(
          {
            receiptUuid:
              salesReceiptUuid.trim(),

            companyUuid:
              context.companyUuid,
          },
          context
        );

      if (!checkoutMountedRef.current) {
        return;
      }

      setReceiptSnapshot(
        authoritativeReceipt
      );

      setPreparedDiscountIntent(null);
      setSelectedBenefit(null);

      await refreshCartSnapshot();
    } catch (error) {
      if (!checkoutMountedRef.current) {
        return;
      }

      setCheckoutError(
        error?.message
        ?? "Impossible de finaliser le checkout."
      );
    } finally {
      checkoutInFlightRef.current =
        false;

      if (checkoutMountedRef.current) {
        setCheckoutLoading(false);
      }
    }
  }


  const cartItems =
    cartSnapshot?.products
    ?? cartSnapshot?.items
    ?? [];


  const loyaltyAccount =
    loyaltySnapshot?.account
    ?? loyaltySnapshot?.loyaltyAccount
    ?? loyaltySnapshot?.loyalty_account
    ?? (
      loyaltySnapshot
      && typeof loyaltySnapshot
        === "object"
      && !Array.isArray(
        loyaltySnapshot
      )
        ? loyaltySnapshot
        : null
    );


  const loyaltyBenefits =
    loyaltySnapshot?.benefits
    ?? loyaltySnapshot
      ?.availableBenefits
    ?? loyaltySnapshot
      ?.available_benefits
    ?? [];


  const cartTotals =
    Object.freeze({
      totalHt:
        cartSnapshot?.total_ht
        ?? cartSnapshot?.totals
          ?.total_ht
        ?? 0,

      totalVat:
        cartSnapshot?.total_vat
        ?? cartSnapshot?.totals
          ?.total_vat
        ?? 0,

      totalTtc:
        cartSnapshot?.total_ttc
        ?? cartSnapshot?.totals
          ?.total_ttc
        ?? 0,
    });


  return (
    <main
      className="retail-pos-workspace"
      data-testid="retail-pos-workspace"
      data-domain-id="retail"
      data-company-uuid={
        context.companyUuid
        ?? ""
      }
      data-activity-uuid={
        context.activityUuid
        ?? ""
      }
      data-publication-id={
        publication
          ?.publicationId
        ?? runtimeConfiguration
          ?.publicationId
        ?? ""
      }
    >
      <header
        className="retail-pos-workspace__header"
      >
        <div>
          <p
            className="retail-pos-workspace__eyebrow"
          >
            Point de vente
          </p>

          <h1>
            Retail POS
          </h1>

          <p
            className="retail-pos-workspace__descriptor"
          >
            {workspaceDescriptor
              ?.id
              ?? "checkout"}
          </p>
        </div>

        <div
          className="retail-pos-workspace__scope"
          aria-label="Contexte POS"
        >
          <span>
            Activité :
            {" "}
            {context.activityUuid
              ?? "non définie"}
          </span>

          <span>
            Société :
            {" "}
            {context.companyUuid
              ?? "non définie"}
          </span>
        </div>
      </header>

      <section
        className="retail-pos-workspace__configuration"
        aria-label="Retail POS configuration"
      >
        <span>
          navigation:
          {navigation.length}
        </span>

        <span>
          actions:
          {actions.length}
        </span>
      </section>

      <section
        className="retail-pos-workspace__capabilities"
        aria-label="Retail POS capabilities"
      >
        <CapabilityBadge
          label="catalog"
          enabled={
            capabilities
              .productResolution
          }
        />

        <CapabilityBadge
          label="search"
          enabled={
            capabilities
              .productSearch
          }
        />

        <CapabilityBadge
          label="cart"
          enabled={
            capabilities.cart
          }
        />

        <CapabilityBadge
          label="checkout"
          enabled={
            capabilities.checkout
          }
        />

        <CapabilityBadge
          label="customer"
          enabled={
            capabilities.customer
          }
        />

        <CapabilityBadge
          label="customer-search"
          enabled={
            capabilities
              .customerSearch
          }
        />

        <CapabilityBadge
          label="loyalty"
          enabled={
            capabilities.loyalty
          }
        />

        <CapabilityBadge
          label="cash"
          enabled={
            capabilities.cashSession
          }
        />

        <CapabilityBadge
          label="receipt"
          enabled={
            capabilities.receipt
          }
        />

        <CapabilityBadge
          label="returns"
          enabled={
            capabilities.returns
          }
        />

        <CapabilityBadge
          label="refunds"
          enabled={
            capabilities.refunds
          }
        />
      </section>

      <div
        className="retail-pos-workspace__grid"
      >
        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--catalog
          "
          aria-labelledby="retail-pos-product-search-title"
          data-testid="retail-pos-product-search"
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-product-search-title"
              >
                Recherche produit
              </h2>

              <p>
                Recherche rapide et scan
                EAN / code produit.
              </p>
            </div>
          </header>

          <form
            className="retail-pos-workspace__search"
            onSubmit={
              (event) => {
                event.preventDefault();
                handleProductSearch();
              }
            }
          >
            <label>
              Recherche produit

              <input
                aria-label="Recherche produit"
                value={searchQuery}
                onChange={
                  (event) =>
                    setSearchQuery(
                      event.target.value
                    )
                }
                autoComplete="off"
              />
            </label>

            <button
              type="submit"
              disabled={
                searchQuery.trim()
                  === ""
                || searchLoading
                || !capabilities
                  .productSearch
              }
            >
              Rechercher
            </button>
          </form>

          <div
            className="retail-pos-workspace__search"
          >
            <label>
              EAN / code produit

              <input
                aria-label="EAN / code produit"
                value={scanCode}
                onChange={
                  (event) =>
                    setScanCode(
                      event.target.value
                    )
                }
                onKeyDown={
                  (event) => {
                    if (
                      event.key
                        === "Enter"
                    ) {
                      handleScan();
                    }
                  }
                }
                autoComplete="off"
              />
            </label>

            <button
              type="button"
              onClick={handleScan}
              disabled={
                scanCode.trim()
                  === ""
              }
            >
              Scanner
            </button>
          </div>
        </section>

        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--results
          "
          aria-labelledby="retail-pos-product-results-title"
          data-testid="retail-pos-product-results"
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-product-results-title"
              >
                Résultats produit
              </h2>

              <p>
                Produit résolu par le
                Runtime POS.
              </p>
            </div>
          </header>

          {searchLoading && (
            <p
              className="retail-pos-workspace__empty"
              role="status"
            >
              Recherche en cours…
            </p>
          )}

          {!searchLoading
            && searchQuery.trim()
              !== ""
            && searchResults.length
              === 0
            && status
              === "search-empty"
            && (
              <p
                className="retail-pos-workspace__empty"
                data-testid="retail-pos-product-search-empty"
              >
                Aucun produit trouvé.
              </p>
            )}

          {searchResults.length > 0 && (
            <ul
              className="retail-pos-workspace__results"
              aria-label="Résultats produits"
              data-testid="retail-pos-product-search-results"
            >
              {searchResults.map(
                (
                  product,
                  index
                ) => {
                  const productKey =
                    product.uuid
                    ?? product.productUuid
                    ?? product.barcode
                    ?? product.sku
                    ?? `product-${index}`;

                  const productLabel =
                    product.label
                    ?? product.designation
                    ?? product.name
                    ?? product.sku
                    ?? product.barcode
                    ?? "Produit";

                  const productReference =
                    product.sku
                    ?? product.reference
                    ?? product.barcode
                    ?? null;

                  const productPrice =
                    product.selling_price_ttc
                    ?? product.sellingPriceTtc
                    ?? product.selling_price
                    ?? null;

                  return (
                    <li
                      key={productKey}
                      className="retail-pos-workspace__result"
                    >
                      <div>
                        <strong>
                          {productLabel}
                        </strong>

                        {productReference && (
                          <small>
                            {productReference}
                          </small>
                        )}

                        {productPrice
                          !== null
                          && productPrice
                            !== undefined
                          && (
                            <span>
                              {productPrice}
                              {" €"}
                            </span>
                          )}
                      </div>

                      <button
                        type="button"
                        onClick={
                          () =>
                            handleSelectProduct(
                              product
                            )
                        }
                        aria-label={
                          `Sélectionner ${productLabel}`
                        }
                      >
                        Sélectionner
                      </button>
                    </li>
                  );
                }
              )}
            </ul>
          )}

          {lastProduct ? (
            <div
              className="retail-pos-workspace__product"
              data-testid="retail-pos-resolved-product"
            >
              <div>
                <small>
                  Produit sélectionné
                </small>

                <strong>
                  {lastProduct.label
                    ?? lastProduct.designation
                    ?? lastProduct.name
                    ?? lastProduct.uuid
                    ?? lastProduct
                      .productUuid
                    ?? "Produit"}
                </strong>
              </div>

              <button
                type="button"
                onClick={
                  handleAddProduct
                }
              >
                Ajouter au panier
              </button>
            </div>
          ) : (
            <p
              className="retail-pos-workspace__empty"
            >
              Aucun produit sélectionné.
            </p>
          )}
        </section>

        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--cart
          "
          aria-labelledby="retail-pos-cart-title"
          data-testid="retail-pos-cart"
          data-enabled={
            String(
              Boolean(
                capabilities.cart
              )
            )
          }
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-cart-title"
              >
                Panier
              </h2>

              <p>
                Panier fourni par le
                Runtime POS.
              </p>
            </div>

            {cartItems.length > 0 && (
              <button
                type="button"
                className="retail-pos-workspace__cart-clear"
                onClick={
                  handleClearCart
                }
                disabled={
                  cartPendingAction
                    !== null
                }
              >
                Vider
              </button>
            )}
          </header>

          {!capabilities.cart && (
            <p
              className="retail-pos-workspace__empty"
            >
              Capability panier
              indisponible.
            </p>
          )}

          {capabilities.cart
            && cartLoading
            && (
              <p
                className="retail-pos-workspace__empty"
                role="status"
                data-testid="retail-pos-cart-loading"
              >
                Chargement du panier…
              </p>
            )}

          {capabilities.cart
            && cartError
            && (
              <div
                className="retail-pos-workspace__cart-error"
                role="alert"
                data-testid="retail-pos-cart-error"
              >
                <span>
                  {cartError}
                </span>

                <button
                  type="button"
                  onClick={
                    refreshCartSnapshot
                  }
                >
                  Réessayer
                </button>
              </div>
            )}

          {capabilities.cart
            && !cartLoading
            && !cartError
            && cartItems.length === 0
            && (
              <p
                className="retail-pos-workspace__empty"
                data-testid="retail-pos-cart-empty"
              >
                Panier vide.
              </p>
            )}

          {capabilities.cart
            && !cartLoading
            && cartItems.length > 0
            && (
              <>
                <ul
                  className="retail-pos-workspace__cart-lines"
                  aria-label="Lignes du panier"
                  data-testid="retail-pos-cart-lines"
                >
                  {cartItems.map(
                    (
                      line,
                      index
                    ) => {
                      const lineId =
                        line.uuid
                        ?? line.productUuid
                        ?? line.activity_product_uuid
                        ?? `cart-line-${index}`;

                      const lineLabel =
                        line.designation
                        ?? line.label
                        ?? line.name
                        ?? "Produit";

                      const quantity =
                        Number(
                          line.quantity
                          ?? 0
                        );

                      const unitPriceTtc =
                        Number(
                          line.selling_price_ttc
                          ?? line.sellingPriceTtc
                          ?? line.unit_ttc
                          ?? line.price
                          ?? 0
                        );

                      const visualLineTotal =
                        quantity
                        * unitPriceTtc;

                      const linePending =
                        cartPendingAction
                          ?.endsWith(
                            `:${lineId}`
                          )
                        ?? false;

                      return (
                        <li
                          key={lineId}
                          className="retail-pos-workspace__cart-line"
                          data-testid="retail-pos-cart-line"
                        >
                          <div
                            className="retail-pos-workspace__cart-product"
                          >
                            <strong>
                              {lineLabel}
                            </strong>

                            <small>
                              {unitPriceTtc
                                .toFixed(2)}
                              {" € / unité"}
                            </small>
                          </div>

                          <div
                            className="retail-pos-workspace__cart-quantity"
                            aria-label={
                              `Quantité ${lineLabel}`
                            }
                          >
                            <button
                              type="button"
                              aria-label={
                                `Diminuer ${lineLabel}`
                              }
                              onClick={
                                () =>
                                  handleDecrementCartLine(
                                    line
                                  )
                              }
                              disabled={
                                linePending
                                || cartPendingAction
                                  === "clear"
                              }
                            >
                              −
                            </button>

                            <span
                              aria-label={
                                `Quantité actuelle ${lineLabel}`
                              }
                            >
                              {quantity}
                            </span>

                            <button
                              type="button"
                              aria-label={
                                `Augmenter ${lineLabel}`
                              }
                              onClick={
                                () =>
                                  handleIncrementCartLine(
                                    line
                                  )
                              }
                              disabled={
                                linePending
                                || cartPendingAction
                                  === "clear"
                              }
                            >
                              +
                            </button>
                          </div>

                          <strong
                            className="retail-pos-workspace__cart-line-total"
                          >
                            {visualLineTotal
                              .toFixed(2)}
                            {" €"}
                          </strong>

                          <button
                            type="button"
                            className="retail-pos-workspace__cart-remove"
                            aria-label={
                              `Supprimer ${lineLabel}`
                            }
                            onClick={
                              () =>
                                handleRemoveCartLine(
                                  line
                                )
                            }
                            disabled={
                              linePending
                              || cartPendingAction
                                === "clear"
                            }
                          >
                            Supprimer
                          </button>
                        </li>
                      );
                    }
                  )}
                </ul>

                <dl
                  className="retail-pos-workspace__cart-totals"
                  data-testid="retail-pos-cart-totals"
                >
                  <div>
                    <dt>
                      Total HT
                    </dt>

                    <dd>
                      {Number(
                        cartTotals.totalHt
                      ).toFixed(2)}
                      {" €"}
                    </dd>
                  </div>

                  <div>
                    <dt>
                      TVA
                    </dt>

                    <dd>
                      {Number(
                        cartTotals.totalVat
                      ).toFixed(2)}
                      {" €"}
                    </dd>
                  </div>

                  <div
                    className="retail-pos-workspace__cart-total-ttc"
                  >
                    <dt>
                      Total TTC
                    </dt>

                    <dd>
                      {Number(
                        cartTotals.totalTtc
                      ).toFixed(2)}
                      {" €"}
                    </dd>
                  </div>
                </dl>
              </>
            )}
        </section>

        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--customer
          "
          aria-labelledby="retail-pos-customer-title"
          data-testid="retail-pos-customer"
          data-enabled={
            String(
              Boolean(
                capabilities.customer
                || capabilities
                  .customerSearch
              )
            )
          }
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-customer-title"
              >
                Client
              </h2>

              <p>
                Identification facultative.
                La vente comptoir reste
                disponible sans client.
              </p>
            </div>
          </header>

          {!selectedCustomer && (
            <>
              <form
                className="retail-pos-workspace__customer-search"
                onSubmit={
                  (event) => {
                    event.preventDefault();
                    handleCustomerSearch();
                  }
                }
              >
                <label>
                  Recherche client

                  <input
                    aria-label="Recherche client"
                    value={
                      customerQuery
                    }
                    onChange={
                      (event) =>
                        setCustomerQuery(
                          event
                            .target
                            .value
                        )
                    }
                    autoComplete="off"
                  />
                </label>

                <button
                  type="submit"
                  disabled={
                    customerQuery
                      .trim()
                      === ""
                    || customerLoading
                    || !capabilities
                      .customerSearch
                  }
                >
                  Rechercher client
                </button>
              </form>

              {!capabilities
                .customerSearch
                && (
                  <p
                    className="retail-pos-workspace__empty"
                  >
                    Recherche client
                    indisponible.
                  </p>
                )}

              {customerLoading && (
                <p
                  role="status"
                  className="retail-pos-workspace__empty"
                  data-testid="retail-pos-customer-loading"
                >
                  Recherche client en cours…
                </p>
              )}

              {customerError && (
                <p
                  role="alert"
                  className="retail-pos-workspace__customer-error"
                  data-testid="retail-pos-customer-error"
                >
                  {customerError}
                </p>
              )}

              {!customerLoading
                && customerResults
                  .length > 0
                && (
                  <ul
                    className="retail-pos-workspace__customer-results"
                    aria-label="Résultats clients"
                    data-testid="retail-pos-customer-results"
                  >
                    {customerResults.map(
                      (
                        customer,
                        index
                      ) => {
                        const customerKey =
                          customer.uuid
                          ?? customer
                            .client_uuid
                          ?? customer.email
                          ?? `customer-${index}`;

                        const customerName =
                          [
                            customer
                              .firstname,
                            customer
                              .lastname,
                          ]
                            .filter(Boolean)
                            .join(" ")
                          || customer.name
                          || customer.email
                          || "Client";

                        return (
                          <li
                            key={
                              customerKey
                            }
                            className="retail-pos-workspace__customer-result"
                          >
                            <div>
                              <strong>
                                {customerName}
                              </strong>

                              {customer.email
                                && (
                                  <small>
                                    {customer.email}
                                  </small>
                                )}

                              {customer
                                .phone_number
                                && (
                                  <small>
                                    {
                                      customer
                                        .phone_number
                                    }
                                  </small>
                                )}
                            </div>

                            <button
                              type="button"
                              aria-label={
                                `Sélectionner client ${customerName}`
                              }
                              onClick={
                                () =>
                                  handleSelectCustomer(
                                    customer
                                  )
                              }
                            >
                              Sélectionner
                            </button>
                          </li>
                        );
                      }
                    )}
                  </ul>
                )}

              {!customerLoading
                && customerQuery
                  .trim() !== ""
                && customerResults
                  .length === 0
                && !customerError
                && capabilities
                  .customerSearch
                && (
                  <p
                    className="retail-pos-workspace__empty"
                    data-testid="retail-pos-customer-empty"
                  >
                    Aucun client trouvé.
                  </p>
                )}

              <div
                className="retail-pos-workspace__walk-in"
                data-testid="retail-pos-walk-in"
              >
                <strong>
                  Vente comptoir
                </strong>

                <span>
                  Aucun client sélectionné.
                </span>
              </div>
            </>
          )}

          {selectedCustomer && (
            <div
              className="retail-pos-workspace__selected-customer"
              data-testid="retail-pos-selected-customer"
            >
              <div>
                <span>
                  Client sélectionné
                </span>

                <strong>
                  {[
                    selectedCustomer
                      .firstname,
                    selectedCustomer
                      .lastname,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    || selectedCustomer
                      .name
                    || selectedCustomer
                      .email
                    || "Client"}
                </strong>

                {selectedCustomer
                  .email
                  && (
                    <small>
                      {
                        selectedCustomer
                          .email
                      }
                    </small>
                  )}
              </div>

              <button
                type="button"
                onClick={
                  handleClearCustomer
                }
              >
                Retirer le client
              </button>
            </div>
          )}
        </section>

        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--loyalty
          "
          aria-labelledby="retail-pos-loyalty-title"
          data-testid="retail-pos-loyalty"
          data-enabled={
            String(
              Boolean(
                capabilities.loyalty
              )
            )
          }
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-loyalty-title"
              >
                Fidélité
              </h2>

              <p>
                Projection du Loyalty
                Engine autoritaire.
              </p>
            </div>
          </header>

          {!selectedCustomer && (
            <p
              className="retail-pos-workspace__empty"
              data-testid="retail-pos-loyalty-walk-in"
            >
              Aucun compte fidélité
              pour une vente comptoir.
            </p>
          )}

          {selectedCustomer
            && !capabilities.loyalty
            && (
              <p
                className="retail-pos-workspace__empty"
              >
                Fidélité indisponible.
              </p>
            )}

          {selectedCustomer
            && loyaltyLoading
            && (
              <p
                role="status"
                className="retail-pos-workspace__empty"
                data-testid="retail-pos-loyalty-loading"
              >
                Chargement de la fidélité…
              </p>
            )}

          {selectedCustomer
            && loyaltyError
            && (
              <p
                role="alert"
                className="retail-pos-workspace__customer-error"
                data-testid="retail-pos-loyalty-error"
              >
                {loyaltyError}
              </p>
            )}

          {selectedCustomer
            && !loyaltyLoading
            && !loyaltyError
            && capabilities.loyalty
            && !loyaltySnapshot
            && (
              <p
                className="retail-pos-workspace__empty"
                data-testid="retail-pos-loyalty-empty"
              >
                Aucun compte fidélité.
              </p>
            )}

          {selectedCustomer
            && loyaltySnapshot
            && (
              <div
                className="retail-pos-workspace__loyalty-snapshot"
                data-testid="retail-pos-loyalty-snapshot"
              >
                {loyaltyAccount && (
                  <dl
                    className="retail-pos-workspace__loyalty-account"
                  >
                    {loyaltyAccount
                      .lifetime_amount
                      !== undefined
                      && (
                        <div>
                          <dt>
                            Cumul fidélité
                          </dt>

                          <dd>
                            {
                              loyaltyAccount
                                .lifetime_amount
                            }
                          </dd>
                        </div>
                      )}

                    {loyaltyAccount
                      .current_cycle_amount
                      !== undefined
                      && (
                        <div>
                          <dt>
                            Cycle actuel
                          </dt>

                          <dd>
                            {
                              loyaltyAccount
                                .current_cycle_amount
                            }
                          </dd>
                        </div>
                      )}

                    {loyaltyAccount
                      .current_cycle_count
                      !== undefined
                      && (
                        <div>
                          <dt>
                            Achats du cycle
                          </dt>

                          <dd>
                            {
                              loyaltyAccount
                                .current_cycle_count
                            }
                          </dd>
                        </div>
                      )}

                    {loyaltyAccount
                      .earned_benefits_count
                      !== undefined
                      && (
                        <div>
                          <dt>
                            Avantages acquis
                          </dt>

                          <dd>
                            {
                              loyaltyAccount
                                .earned_benefits_count
                            }
                          </dd>
                        </div>
                      )}
                  </dl>
                )}

                <div>
                  <strong>
                    Avantages disponibles
                  </strong>

                  {Array.isArray(
                    loyaltyBenefits
                  )
                    && loyaltyBenefits
                      .length > 0
                    ? (
                      <ul
                        className="retail-pos-workspace__loyalty-benefits"
                        aria-label="Avantages fidélité disponibles"
                      >
                        {loyaltyBenefits.map(
                          (
                            benefit,
                            index
                          ) => (
                            <li
                              key={
                                benefit.uuid
                                ?? benefit.id
                                ?? `benefit-${index}`
                              }
                            >
                              {
                                benefit.label
                                ?? benefit.name
                                ?? benefit
                                  .benefit_type
                                ?? "Avantage fidélité"
                              }
                            </li>
                          )
                        )}
                      </ul>
                    )
                    : (
                      <p
                        className="retail-pos-workspace__empty"
                      >
                        Aucun avantage disponible.
                      </p>
                    )}
                </div>
              </div>
            )}
        </section>

        <section
          className="
            retail-pos-workspace__panel
            retail-pos-workspace__panel--discounts
          "
          aria-labelledby="retail-pos-discounts-title"
          data-testid="retail-pos-discounts"
        >
          <header
            className="retail-pos-workspace__panel-header"
          >
            <div>
              <h2
                id="retail-pos-discounts-title"
              >
                Remises / Avoir
              </h2>

              <p>
                Préparation UX avant création
                du ticket de vente.
              </p>
            </div>
          </header>

          <section
            className="retail-pos-workspace__discount-section"
            aria-labelledby="retail-pos-discount-intent-title"
          >
            <h3
              id="retail-pos-discount-intent-title"
            >
              Remise
            </h3>

            <p
              className="retail-pos-workspace__discount-boundary"
              data-testid="retail-pos-discount-boundary"
            >
              La remise sera appliquée par
              l'autorité Receipt après création
              du ticket lors du checkout.
            </p>

            <form
              className="retail-pos-workspace__discount-form"
              onSubmit={
                handlePrepareDiscountIntent
              }
            >
              <label>
                Type de remise

                <select
                  aria-label="Type de remise"
                  value={
                    discountType
                  }
                  onChange={
                    (event) => {
                      setDiscountType(
                        event.target.value
                      );

                      setPreparedDiscountIntent(
                        null
                      );
                    }
                  }
                >
                  <option
                    value="percent"
                  >
                    Pourcentage
                  </option>

                  <option
                    value="amount"
                  >
                    Montant
                  </option>
                </select>
              </label>

              <label>
                Appliquer sur

                <select
                  aria-label="Cible de la remise"
                  value={
                    discountScope
                  }
                  onChange={
                    (event) => {
                      setDiscountScope(
                        event.target.value
                      );

                      setSelectedDiscountTarget(
                        ""
                      );

                      setPreparedDiscountIntent(
                        null
                      );
                    }
                  }
                >
                  <option
                    value="cart"
                  >
                    Ticket total
                  </option>

                  <option
                    value="line"
                  >
                    Un article
                  </option>
                </select>
              </label>

              <label>
                Valeur

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={
                    discountType
                      === "percent"
                      ? "100"
                      : undefined
                  }
                  aria-label="Valeur de la remise"
                  value={
                    discountValue
                  }
                  onChange={
                    (event) => {
                      setDiscountValue(
                        event.target.value
                      );

                      setPreparedDiscountIntent(
                        null
                      );
                    }
                  }
                />
              </label>

              {discountScope
                === "line"
                && (
                  <label>
                    Article cible

                    <select
                      aria-label="Article cible de la remise"
                      value={
                        selectedDiscountTarget
                      }
                      onChange={
                        (event) => {
                          setSelectedDiscountTarget(
                            event.target.value
                          );

                          setPreparedDiscountIntent(
                            null
                          );
                        }
                      }
                    >
                      <option value="">
                        Sélectionner un article
                      </option>

                      {cartItems.map(
                        (
                          item,
                          index
                        ) => {
                          const key =
                            String(
                              item.uuid
                              ?? item
                                .productUuid
                              ?? item
                                .activity_product_uuid
                              ?? `cart-line-${index}`
                            );

                          const label =
                            item.label
                            ?? item.designation
                            ?? item.name
                            ?? item.wording
                            ?? item.sku
                            ?? "Article";

                          return (
                            <option
                              key={key}
                              value={key}
                            >
                              {label}
                            </option>
                          );
                        }
                      )}
                    </select>
                  </label>
                )}

              <button
                type="submit"
                disabled={
                  cartItems.length === 0
                  || String(
                    discountValue
                  ).trim() === ""
                  || (
                    discountScope
                      === "line"
                    && selectedDiscountTarget
                      === ""
                  )
                }
              >
                Préparer la remise
              </button>
            </form>

            {preparedDiscountIntent && (
              <div
                className="retail-pos-workspace__discount-intent"
                data-testid="retail-pos-prepared-discount"
              >
                <div>
                  <strong>
                    Remise préparée
                  </strong>

                  <span>
                    {
                      preparedDiscountIntent
                        .scope
                        === "cart"
                        ? "Ticket total"
                        : "Article"
                    }
                    {" — "}
                    {
                      preparedDiscountIntent
                        .value
                    }
                    {
                      preparedDiscountIntent
                        .type
                        === "percent"
                        ? " %"
                        : " €"
                    }
                  </span>
                </div>

                <button
                  type="button"
                  onClick={
                    handleClearDiscountIntent
                  }
                >
                  Annuler la remise préparée
                </button>
              </div>
            )}
          </section>

          <section
            className="retail-pos-workspace__discount-section"
            aria-labelledby="retail-pos-benefit-intent-title"
          >
            <h3
              id="retail-pos-benefit-intent-title"
            >
              Avantage fidélité
            </h3>

            {!selectedCustomer && (
              <p
                className="retail-pos-workspace__empty"
              >
                Sélectionner un client pour
                consulter ses avantages.
              </p>
            )}

            {selectedCustomer
              && loyaltyBenefits.length === 0
              && (
                <p
                  className="retail-pos-workspace__empty"
                >
                  Aucun avantage fidélité
                  disponible.
                </p>
              )}

            {selectedCustomer
              && loyaltyBenefits.length > 0
              && (
                <ul
                  className="retail-pos-workspace__discount-benefits"
                  aria-label="Avantages fidélité sélectionnables"
                >
                  {loyaltyBenefits.map(
                    (
                      benefit,
                      index
                    ) => {
                      const benefitKey =
                        benefit.uuid
                        ?? benefit.id
                        ?? `discount-benefit-${index}`;

                      const benefitLabel =
                        benefit.label
                        ?? benefit.name
                        ?? benefit
                          .benefit_type
                        ?? "Avantage fidélité";

                      const redeemableType =
                        benefit
                          .benefit_type
                          === "percent_discount"
                        || benefit
                          .benefit_type
                          === "fixed_amount";

                      const available =
                        benefit.status
                          === undefined
                        || benefit.status
                          === null
                        || benefit.status
                          === "available";

                      const selectable =
                        redeemableType
                        && available;

                      return (
                        <li
                          key={
                            benefitKey
                          }
                        >
                          <div>
                            <strong>
                              {benefitLabel}
                            </strong>

                            {benefit
                              .benefit_value
                              !== undefined
                              && (
                                <small>
                                  {
                                    benefit
                                      .benefit_value
                                  }
                                  {
                                    benefit
                                      .benefit_type
                                      === "percent_discount"
                                      ? " %"
                                      : benefit
                                        .benefit_type
                                        === "fixed_amount"
                                        ? " €"
                                        : ""
                                  }
                                </small>
                              )}
                          </div>

                          <button
                            type="button"
                            disabled={
                              !selectable
                            }
                            aria-label={
                              `Sélectionner avantage ${benefitLabel}`
                            }
                            onClick={
                              () =>
                                handleSelectLoyaltyBenefit(
                                  benefit
                                )
                            }
                          >
                            Sélectionner
                          </button>
                        </li>
                      );
                    }
                  )}
                </ul>
              )}

            {selectedBenefit && (
              <div
                className="retail-pos-workspace__discount-intent"
                data-testid="retail-pos-selected-benefit"
              >
                <div>
                  <strong>
                    Avantage sélectionné
                  </strong>

                  <span>
                    {
                      selectedBenefit.label
                      ?? selectedBenefit.name
                      ?? selectedBenefit
                        .benefit_type
                      ?? "Avantage fidélité"
                    }
                  </span>
                </div>

                <button
                  type="button"
                  onClick={
                    handleClearLoyaltyBenefit
                  }
                >
                  Retirer l'avantage
                </button>
              </div>
            )}

            <p
              className="retail-pos-workspace__discount-boundary"
              data-testid="retail-pos-loyalty-redeem-boundary"
            >
              L'avantage sélectionné ne sera
              consommé qu'après création du
              SalesReceipt pendant le checkout.
            </p>
          </section>

          <section
            className="retail-pos-workspace__discount-section"
            aria-labelledby="retail-pos-credit-title"
            data-testid="retail-pos-credit-unavailable"
          >
            <h3
              id="retail-pos-credit-title"
            >
              Avoir
            </h3>

            <p
              className="retail-pos-workspace__empty"
            >
              Contrat Avoir non disponible.
              La recherche, la consommation
              et le solde d'un avoir restent
              à certifier avec Return / Refund
              et Checkout / Payment.
            </p>
          </section>
        </section>

        <section
          className="retail-pos-workspace__panel"
          aria-labelledby="retail-pos-checkout-title"
          data-testid="retail-pos-checkout"
          aria-busy={
            checkoutLoading
              ? "true"
              : "false"
          }
        >
          <header>
            <h2
              id="retail-pos-checkout-title"
            >
              Checkout
            </h2>

            <p>
              Le Workspace déclenche le checkout
              sans devenir propriétaire du Receipt.
            </p>
          </header>

          {!capabilities.checkout && (
            <p
              role="status"
              data-testid="retail-pos-checkout-unavailable"
            >
              Capability checkout indisponible.
            </p>
          )}

          {capabilities.checkout
            && !receiptSnapshot
            && (
              <button
                type="button"
                onClick={
                  handleCheckout
                }
                disabled={
                  checkoutLoading
                  || cartItems.length === 0
                }
                data-testid="retail-pos-checkout-submit"
              >
                {checkoutLoading
                  ? "Checkout en cours…"
                  : "Finaliser le checkout"}
              </button>
            )}

          {checkoutLoading && (
            <p
              role="status"
              data-testid="retail-pos-checkout-loading"
            >
              Checkout en cours…
            </p>
          )}

          {checkoutError && (
            <p
              role="alert"
              data-testid="retail-pos-checkout-error"
            >
              {checkoutError}
            </p>
          )}

          {receiptSnapshot && (
            <div
              data-testid="retail-pos-receipt-snapshot"
            >
              <strong>
                Checkout terminé
              </strong>

              <p>
                Receipt autoritaire chargé.
              </p>

              <RetailPOSTicketWorkspace
                runtime={
                  runtime
                }
                context={
                  context
                }
                receiptSnapshot={
                  receiptSnapshot
                }
              />

              <RetailPOSReturnRefundWorkspace
                runtime={
                  runtime
                }
                context={
                  context
                }
                receiptSnapshot={
                  receiptSnapshot
                }
              />

              <button
                type="button"
                onClick={
                  () => {
                    setReceiptSnapshot(null);
                    setCheckoutError(null);
                  }
                }
                data-testid="retail-pos-new-checkout"
              >
                Nouveau checkout
              </button>
            </div>
          )}

          {receiptSnapshot && (
            <RetailPOSPaymentWorkspace
              runtime={
                runtime
              }
              context={
                context
              }
              receiptSnapshot={
                receiptSnapshot
              }
              onReceiptSnapshotChange={
                setReceiptSnapshot
              }
            />
          )}

        </section>

        {capabilities.cashSession && (
          <RetailPOSCashSessionWorkspace
            runtime={
              runtime
            }
            context={
              context
            }
          />
        )}

        <WorkspaceZone
          testId="retail-pos-actions"
          title="Actions POS"
          description={
            `${actions.length} action(s) configurée(s).`
          }
        />
      </div>

      {status && (
        <div
          className="retail-pos-workspace__status"
          role="status"
          aria-live="polite"
          data-retail-pos-status={
            status
          }
        >
          {status}
        </div>
      )}
    </main>
  );
}


function CapabilityBadge({
  label,
  enabled,
}) {
  return (
    <span
      className="retail-pos-workspace__capability"
      data-enabled={
        enabled
          ? "true"
          : "false"
      }
    >
      {label}:
      {String(Boolean(enabled))}
    </span>
  );
}


CapabilityBadge.propTypes = {
  label:
    PropTypes.string
      .isRequired,

  enabled:
    PropTypes.bool,
};


function WorkspaceZone({
  testId,
  title,
  description,
  enabled,
}) {
  return (
    <section
      className="retail-pos-workspace__panel"
      data-testid={testId}
      data-enabled={
        enabled === undefined
          ? undefined
          : String(Boolean(enabled))
      }
      aria-labelledby={
        `${testId}-title`
      }
    >
      <header
        className="retail-pos-workspace__panel-header"
      >
        <div>
          <h2
            id={`${testId}-title`}
          >
            {title}
          </h2>

          <p>
            {description}
          </p>
        </div>
      </header>
    </section>
  );
}


WorkspaceZone.propTypes = {
  testId:
    PropTypes.string
      .isRequired,

  title:
    PropTypes.string
      .isRequired,

  description:
    PropTypes.string
      .isRequired,

  enabled:
    PropTypes.bool,
};


RetailPOSWorkspace.propTypes = {
  runtime:
    PropTypes.object
      .isRequired,

  resolvedPOSActivityDomain:
    PropTypes.object,

  runtimeConfiguration:
    PropTypes.object,

  publication:
    PropTypes.object,

  workspaceDescriptor:
    PropTypes.object,

  navigation:
    PropTypes.array,

  actions:
    PropTypes.array,
};
